import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Ticket, CreditCard, ShieldCheck, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

export default function Checkout() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/settings/public").then(({ data }) => setSettings(data)).catch(() => {}); }, []);

  const paypalOptions = useMemo(
    () => (settings?.paypal_client_id ? { clientId: settings.paypal_client_id, currency: "EUR", intent: "capture" } : null),
    [settings?.paypal_client_id]
  );

  if (!settings) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#E06D53]" /></div>;

  const plan = settings.plans.find((p) => p.id === planId);
  if (!plan) return <div className="max-w-lg mx-auto px-5 py-24 text-center"><p>{t("checkout.notFound")}</p><Button className="mt-4 rounded-full" onClick={() => navigate("/tarifs")}>{t("checkout.seePricing")}</Button></div>;

  const payStripe = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { plan_id: plan.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || t("checkout.payError"));
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-5 py-14 paper-noise min-h-[70vh]">
      <button onClick={() => navigate("/tarifs")} className="flex items-center gap-1 text-sm text-[#5C5854] hover:text-[#E06D53] mb-6" data-testid="checkout-back">
        <ArrowLeft className="w-4 h-4" /> {t("checkout.back")}
      </button>
      <h1 className="font-serif text-4xl md:text-5xl tracking-tighter text-[#2C2A28] mb-10">{t("checkout.cartTitle")}</h1>

      <div className="grid md:grid-cols-5 gap-8">
        <motion.div className="md:col-span-2 bg-white rounded-2xl border border-[#E5E0D8] p-7 h-fit"
          initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} data-testid="checkout-summary">
          <Ticket className="w-8 h-8 text-[#E06D53] mb-4" />
          <h3 className="text-xl font-medium text-[#2C2A28]">{plan.name}</h3>
          <p className="text-sm text-[#8C857B] mt-1">{t("checkout.access", { days: plan.days })}</p>
          <div className="border-t border-[#E5E0D8] my-5" />
          <div className="flex justify-between text-sm text-[#5C5854] mb-2"><span>{t("checkout.subtotal")}</span><span>{(plan.amount / 100).toFixed(2)}€</span></div>
          <div className="flex justify-between items-end mt-4">
            <span className="text-[#2C2A28] font-medium">{t("checkout.total")}</span>
            <span className="font-serif text-4xl text-[#1D4E89]">{(plan.amount / 100).toFixed(2)}€</span>
          </div>
        </motion.div>

        <motion.div className="md:col-span-3 bg-white rounded-2xl border border-[#E5E0D8] p-7"
          initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }}>
          <h3 className="text-lg font-medium text-[#2C2A28] mb-1">{t("checkout.paymentMode")}</h3>
          <p className="text-sm text-[#8C857B] mb-6 flex items-center gap-1"><ShieldCheck className="w-4 h-4 text-[#2D6A4F]" /> {t("checkout.secure")}</p>

          <Button onClick={payStripe} disabled={loading}
            className="w-full rounded-full h-12 bg-[#635BFF] hover:bg-[#514bcc] text-white text-base" data-testid="pay-stripe-btn">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CreditCard className="w-5 h-5 mr-2" /> {t("checkout.payCard")}</>}
          </Button>
          <p className="text-xs text-center text-[#8C857B] mt-2">{t("checkout.cardProviders")}</p>

          {settings.paypal_enabled && settings.paypal_client_id ? (
            <>
              <div className="flex items-center gap-3 my-6">
                <div className="flex-1 h-px bg-[#E5E0D8]" /><span className="text-xs text-[#8C857B] uppercase tracking-widest">{t("common.or")}</span><div className="flex-1 h-px bg-[#E5E0D8]" />
              </div>
              <div data-testid="paypal-container">
                <PayPalScriptProvider options={paypalOptions}>
                  <PayPalButtons style={{ shape: "pill", color: "gold", layout: "vertical", label: "pay" }}
                    createOrder={async () => {
                      const { data } = await api.post("/paypal/create-order", { plan_id: plan.id, origin_url: window.location.origin });
                      return data.order_id;
                    }}
                    onApprove={async (data) => {
                      try {
                        await api.post(`/paypal/capture/${data.orderID}`);
                        navigate(`/payment/success?provider=paypal`);
                      } catch { toast.error(t("checkout.paypalError")); }
                    }}
                    onError={() => toast.error(t("checkout.paypalErr2"))} />
                </PayPalScriptProvider>
              </div>
            </>
          ) : (
            <div className="mt-6 text-xs text-[#8C857B] bg-[#FAF9F6] border border-[#E5E0D8] rounded-xl p-3" data-testid="paypal-disabled-note">
              {t("checkout.paypalSoon")}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
